import React from 'react';
import clsx from 'clsx';

import {
  ProductTile,
  ProductTileContent,
  ProductDetails,
  ProductTileInfoButton,
  ProductTitle,
  ProductImage,
  ProductSubtitle,
  ProductUpchargeBadge,
  ProductTileCtaSection,
  ProductTileFooter
} from '@shared-components/molecules/ProductTile';
import Button, { buttonVariantClasses } from '@shared-components/atoms/Button';
import Badge from '@shared-components/atoms/Badge';

import QuantityPickerCTA from '@noqo/clients/web/shared-components/molecules/QuantityPicker';
import { useDecision } from '@optimizely/react-sdk';
import css from './MultiRecipeSelection.module.css';
import useShowNewBadge from '@/menu/hooks/useShowNewBadge';

export type RecipeTileData = {
  id: string;
  isCyo: boolean;
  name: string;
  description?: string;
  outOfStock?: boolean;
  imageURL?: string;
  sizeCrustSelection: string;
  upchargeAmount: string;
  formattedPrice: string;
  onCustomize: () => void;
  onAddToDeal: (quantity?: number, sizeOverride?: string) => void;
  nutritionInfo: string;
  showSizeAndCrustSelection: boolean;
  primaryCtaButtonText: string;
  showGetStartedExperience: boolean;
  shouldShowPrice: boolean;
  shouldShowUpchargeBadge: boolean;
  shouldShowActionRow: boolean;
  useVariantName?: boolean;
  sizeOptions?: {
    name: string;
    value: string;
    upcharge?: number;
  }[];
};

type MultiRecipeSelectionTileListProps = {
  product: RecipeTileData;
  stepName?: string;
  isLoading?: boolean;
  isSingleStepDeal?: boolean;
};

function MultiRecipeSelectionTile({
  product, stepName, isLoading = false, isSingleStepDeal
}: MultiRecipeSelectionTileListProps): JSX.Element {
  const [quantity, setQuantity] = React.useState(1);
  const [{ enabled: quantityPickerSingleStepDealsEnabled }] = useDecision('fr-dtg-1060-quantity-picker-single-step-deals');
  const {
    id,
    isCyo,
    name,
    description,
    outOfStock,
    sizeCrustSelection,
    imageURL,
    upchargeAmount,
    formattedPrice,
    nutritionInfo,
    onCustomize,
    onAddToDeal,
    showSizeAndCrustSelection,
    primaryCtaButtonText,
    showGetStartedExperience,
    shouldShowPrice,
    shouldShowUpchargeBadge,
    shouldShowActionRow,
    useVariantName
  } = product;

  const { shouldShowNewBadge } = useShowNewBadge(id);

  const showQuantityPicker = quantityPickerSingleStepDealsEnabled && isSingleStepDeal;

  const handleAddToDeal = () => {
    onAddToDeal(quantity);
  }

  let title = name;
  if (isCyo && !useVariantName) {
    title = `${name} ${stepName ?? ''}`;
  }
  const testId = isCyo ? 'cyo-pizza-tile' : 'upcharge-recipe-tile';
  const buttonVariantClass = isCyo ? 'button--primary' : 'button--secondary';
  const actionRowContent = showGetStartedExperience ? (
    <Button
      className={
        clsx(
          buttonVariantClasses.button,
          buttonVariantClasses[buttonVariantClass],
          css['product-tile__get-started-cta']
        )
      }
      aria-label={`Get started ${name} ${stepName}`}
      disabled={outOfStock}
      onClick={onCustomize}
    >
      Get Started
    </Button>
  ) : (
    <>
      <Button
        className={css['product-tile__cta-link']}
        aria-label={`Customize ${name}`}
        onClick={onCustomize}
        variant="link"
        disabled={outOfStock}
      >
        Customize
      </Button>
      {showQuantityPicker ? (
        <QuantityPickerCTA
          id={`quantity-picker-${id}`}
          name={name}
          value={`${quantity}`}
          size="small"
          onQuantityChange={(newQuantity) => {
            setQuantity(Number((newQuantity.currentTarget as HTMLInputElement)?.value));
          }}
          onSubmit={handleAddToDeal}
        />
      ) : (
        <Button
          variant="secondary"
          onClick={() => onAddToDeal()}
          aria-label={`${primaryCtaButtonText} ${name}`}
          disabled={outOfStock}
          className={css['product-tile__add-to-deal-button']}
          isLoading={isLoading}
        >
          {primaryCtaButtonText}
        </Button>
      )}
    </>
  );

  return (
    <ProductTile testId={testId}>
      <ProductTileContent>
        <ProductImage imageURL={imageURL} imageAltText={name} productName={name} />
        <ProductDetails>
          <div className={css['product-tile__product-name-crust-container']}>
            <ProductTitle>{title}</ProductTitle>
            {!outOfStock && (
              <ProductTileInfoButton productName={title} productDescription={description} />
            )}
          </div>
          <div className={css['product-tile__badges-container']}>
            {outOfStock && (<Badge variant="disabled">Sold out</Badge>)}
            {shouldShowNewBadge && <Badge variant="primary">New</Badge>}
          </div>
          {showSizeAndCrustSelection && <ProductSubtitle className={css['product-tile__product-size-crust']}>{sizeCrustSelection}</ProductSubtitle>}
          {shouldShowPrice && <ProductSubtitle>Starting at {formattedPrice}</ProductSubtitle>}
          {shouldShowUpchargeBadge && <ProductUpchargeBadge upchargeAmount={upchargeAmount} />}
        </ProductDetails>
        {shouldShowActionRow && <ProductTileCtaSection className={showGetStartedExperience ? css['product-tile__cta-button--cyo'] : ''}>
          {actionRowContent}
        </ProductTileCtaSection>}
      </ProductTileContent>
      {(nutritionInfo && !showGetStartedExperience) && (<ProductTileFooter>{nutritionInfo}</ProductTileFooter>)}
    </ProductTile>
  );
}

export default React.memo(MultiRecipeSelectionTile);
